/**
 * Share & Earn client — calls Cloud Functions; never writes wallet fields itself.
 */
import { httpsCallable } from "firebase/functions";
import { getFunctionsAsia } from "./firebase/functions";
import { detectPriceRegion } from "./shop/regionalPricing";
import { shareNative, canvasToPngFile, type NativeShareResult } from "./nativeShare";
import {
  buildPlayStoreReferralUrl,
  downloadCtaLine,
} from "./shareCopy";

const REF_KEY = "uptReferralCode";

export type ReferralWalletView = {
  referralCode: string;
  referredBy: string | null;
  referralRegion: string | null;
  wallet: {
    pendingInr: number;
    availableInr: number;
    lifetimeEarnedInr: number;
    lifetimeWithdrawnInr: number;
    lifetimeRequestedInr: number;
    kycName: string | null;
    kycUpi: string | null;
    hasKyc: boolean;
  };
  pendingUnlocks: Array<{ amountInr: number; unlockAtMs: number; paymentId: string }>;
  withdrawals: Array<{ id: string; amountInr: number; status: string; createdAtMs: number | null }>;
  rules: {
    commissionInr: number;
    holdDays: number;
    minWithdrawInr: number;
    geography: string;
    payoutMode: string;
  };
};

function fnErrorMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const msg = String((e as { message: string }).message);
    return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\([^)]*\)\s*$/, "").trim() || msg;
  }
  return e instanceof Error ? e.message : String(e);
}

export function rememberReferralCode(code: string): void {
  const c = code.trim().toUpperCase();
  if (!c) return;
  try {
    localStorage.setItem(REF_KEY, c);
  } catch {
    /* ignore */
  }
}

export function getRememberedReferralCode(): string | null {
  try {
    return localStorage.getItem(REF_KEY)?.trim().toUpperCase() || null;
  } catch {
    return null;
  }
}

/** Capture ?ref=CODE from URL (Share & Earn). */
export function consumeReferralParam(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref")?.trim().toUpperCase();
    if (code) {
      rememberReferralCode(code);
      params.delete("ref");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", next);
      return code;
    }
  } catch {
    /* ignore */
  }
  return getRememberedReferralCode();
}

export function buildReferralShareUrl(code: string): string {
  // Share sheet "url" field = Play (Install Referrer). Full text also has Microsoft Store.
  return buildPlayStoreReferralUrl(code);
}

export function buildReferralShareText(code: string, displayName: string): string {
  const name = displayName.trim() || "Your friend";
  const c = code.trim().toUpperCase();
  return (
    `${name} ne tumhe Ultimate Piano Tutor invite kiya! 🎹\n\n` +
    `Music lovers ke liye bhi — classic piano asani se seekho, guided classes, MIDI keyboard, exams & certificate.\n\n` +
    downloadCtaLine(c) +
    `\n\nJab tum Full Course kharidoge, ${name} ko thank-you reward milta hai.\n` +
    `#UltimatePianoTutor #LearnPiano #ShareAndEarn`
  );
}

function renderReferralShareCard(code: string, displayName: string): HTMLCanvasElement {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#031022");
  bg.addColorStop(1, "#0a3a2a");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = "#8ad6ff";
  ctx.font = "700 26px Oxanium, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ULTIMATE PIANO TUTOR", W / 2, 110);
  ctx.fillStyle = "#ffd56a";
  ctx.font = "800 42px Oxanium, system-ui, sans-serif";
  ctx.fillText("SHARE & EARN", W / 2, 180);

  const name = (displayName || "Piano Cadet").trim().slice(0, 28);
  ctx.fillStyle = "#fff6e8";
  ctx.font = "700 48px Oxanium, system-ui, sans-serif";
  ctx.fillText(name, W / 2, 280);

  ctx.fillStyle = "rgba(242,248,255,0.85)";
  ctx.font = "600 28px Sora, system-ui, sans-serif";
  ctx.fillText("invites you to learn piano easily", W / 2, 340);

  ctx.fillStyle = "rgba(10,42,88,0.9)";
  roundRectPath(ctx, 120, 420, W - 240, 280, 24);
  ctx.fill();
  ctx.strokeStyle = "rgba(77,255,176,0.55)";
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.fillStyle = "#4dffb0";
  ctx.font = "700 24px Oxanium, system-ui, sans-serif";
  ctx.fillText("YOUR CODE", W / 2, 490);
  ctx.fillStyle = "#ffd56a";
  ctx.font = "800 64px Oxanium, system-ui, sans-serif";
  ctx.fillText(code, W / 2, 580);
  ctx.fillStyle = "#f2f8ff";
  ctx.font = "600 26px Sora, system-ui, sans-serif";
  ctx.fillText("Friend buys Full Course → you earn ₹250", W / 2, 650);

  ctx.fillStyle = "#fff6e8";
  ctx.font = "700 30px Oxanium, system-ui, sans-serif";
  ctx.fillText("Free on Google Play + Microsoft Store", W / 2, 800);
  ctx.fillStyle = "#ffd56a";
  ctx.font = "800 40px Oxanium, system-ui, sans-serif";
  ctx.fillText(`CODE ${code}`, W / 2, 860);
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "600 20px Sora, system-ui, sans-serif";
  ctx.fillText("Play · play.google.com/…ultimateclassic", W / 2, 910);
  ctx.fillText("Windows · apps.microsoft.com/detail/9P9WSTJSPC2G", W / 2, 945);
  ctx.fillStyle = "#4dffb0";
  ctx.font = "700 22px Oxanium, system-ui, sans-serif";
  ctx.fillText("Sign in pe code auto / manual fill", W / 2, 995);

  ctx.fillStyle = "rgba(242,248,255,0.4)";
  ctx.font = "500 18px Sora, system-ui, sans-serif";
  ctx.fillText("Android · Windows  ·  LOTI Games and Apps", W / 2, 1260);
  return canvas;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export async function shareReferralInvite(
  code: string,
  displayName: string,
): Promise<NativeShareResult> {
  const canvas = renderReferralShareCard(code, displayName);
  const file = await canvasToPngFile(canvas, `upt-referral-${code}.png`);
  return shareNative({
    title: "Share & Earn · Ultimate Piano Tutor",
    text: buildReferralShareText(code, displayName),
    url: buildReferralShareUrl(code),
    file,
    downloadName: `upt-referral-${code}.png`,
  });
}

export async function fetchReferralWallet(): Promise<ReferralWalletView> {
  const fn = httpsCallable(getFunctionsAsia(), "getReferralWallet");
  const res = await fn({});
  return res.data as ReferralWalletView;
}

export async function claimStoredReferralIfNeeded(): Promise<{ claimed: boolean; message?: string }> {
  const code = getRememberedReferralCode();
  if (!code) return { claimed: false };
  return claimReferralCode(code);
}

/** Apply a referral code for the signed-in Google user (India). */
export async function claimReferralCode(code: string): Promise<{ claimed: boolean; message?: string }> {
  const c = code.trim().toUpperCase();
  if (!c) return { claimed: false };
  rememberReferralCode(c);
  const regionId = detectPriceRegion();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  try {
    const fn = httpsCallable(getFunctionsAsia(), "claimReferralCode");
    await fn({ code: c, regionId, timezone });
    return { claimed: true };
  } catch (e) {
    return { claimed: false, message: fnErrorMessage(e) };
  }
}

export async function saveReferralKyc(name: string, upi: string): Promise<void> {
  try {
    const fn = httpsCallable(getFunctionsAsia(), "saveReferralKyc");
    await fn({ name, upi });
  } catch (e) {
    throw new Error(fnErrorMessage(e));
  }
}

export async function requestReferralWithdraw(amountInr: number): Promise<{ message: string }> {
  try {
    const fn = httpsCallable(getFunctionsAsia(), "requestReferralWithdraw");
    const res = await fn({ amountInr });
    const data = res.data as { message?: string };
    return { message: data.message || "Withdraw requested." };
  } catch (e) {
    throw new Error(fnErrorMessage(e));
  }
}
